import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import MetricSelectionTree from './components/MetricSelectionTree';
import DateRangePicker from './components/DateRangePicker';
import DepartmentFilterMatrix from './components/DepartmentFilterMatrix';
import ExportFormatSelector from './components/ExportFormatSelector';
import ReportPreviewPanel from './components/ReportPreviewPanel';
import TemplateLibrary from './components/TemplateLibrary';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';

import * as XLSX from 'xlsx';
import Papa from 'papaparse';

const CustomComplianceReportBuilder = () => {
  const [selectedMetrics, setSelectedMetrics] = useState({
    pngEmploymentAct: true,
    nasfundAdherence: true,
    swtAccuracy: true,
    leavePolicyViolations: true,
    payrollCompliance: true
  });
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
    preset: 'monthly'
  });
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      generatePreview();
    }
    checkSubscription();
  }, [selectedMetrics, dateRange, selectedDepartments]);

  const generatePreview = async () => {
    try {
      setPreviewLoading(true);
      setError('');

      const previewData = {
        metrics: selectedMetrics,
        dateRange,
        departments: selectedDepartments,
        summary: {
          overallScore: 87.5,
          totalViolations: 23,
          criticalViolations: 5,
          departmentsAnalyzed: selectedDepartments?.length || 7
        },
        charts: generateChartData(),
        tables: generateTableData()
      };

      setReportData(previewData);
    } catch (err) {
      setError(err?.message || 'Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const generateChartData = () => {
    return {
      complianceTrend: [
        { month: 'Jan', score: 85 },
        { month: 'Feb', score: 87 },
        { month: 'Mar', score: 86 },
        { month: 'Apr', score: 88 }
      ],
      violationsByType: [
        { type: 'Leave Policy', count: 8 },
        { type: 'Payroll', count: 5 },
        { type: 'Contract', count: 10 }
      ]
    };
  };

  const generateTableData = () => {
    return [
      {
        department: 'HR',
        complianceScore: 92,
        violations: 3,
        employees: 45
      },
      {
        department: 'Finance',
        complianceScore: 88,
        violations: 5,
        employees: 32
      },
      {
        department: 'Operations',
        complianceScore: 85,
        violations: 8,
        employees: 78
      }
    ];
  };

  const handleExportReport = async () => {
    try {
      setLoading(true);
      setError('');

      if (!dateRange?.startDate || !dateRange?.endDate) {
        setError('Please select a date range');
        return;
      }

      if (exportFormat === 'excel') {
        exportToExcel();
      } else if (exportFormat === 'csv') {
        exportToCSV();
      } else if (exportFormat === 'pdf') {
        exportToPDF();
      } else if (exportFormat === 'powerpoint') {
        exportToPowerPoint();
      }
    } catch (err) {
      setError(err?.message || 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const wb = XLSX?.utils?.book_new();

    const summaryData = [
      ['Custom Compliance Report'],
      ['Generated', new Date()?.toLocaleDateString()],
      ['Period', `${dateRange?.startDate} to ${dateRange?.endDate}`],
      ['Departments', selectedDepartments?.join(', ') || 'All'],
      [],
      ['Overall Compliance Score', `${reportData?.summary?.overallScore}%`],
      ['Total Violations', reportData?.summary?.totalViolations],
      ['Critical Violations', reportData?.summary?.criticalViolations]
    ];
    const summarySheet = XLSX?.utils?.aoa_to_sheet(summaryData);
    XLSX?.utils?.book_append_sheet(wb, summarySheet, 'Summary');

    if (reportData?.tables?.length > 0) {
      const deptSheet = XLSX?.utils?.json_to_sheet(
        reportData?.tables?.map(d => ({
          'Department': d?.department,
          'Compliance Score': d?.complianceScore,
          'Violations': d?.violations,
          'Employees': d?.employees
        }))
      );
      XLSX?.utils?.book_append_sheet(wb, deptSheet, 'Department Analysis');
    }

    XLSX?.writeFile(wb, `Custom_Compliance_Report_${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
  };

  const exportToCSV = () => {
    const csvData = reportData?.tables?.map(d => ({
      'Department': d?.department,
      'Compliance Score': d?.complianceScore,
      'Violations': d?.violations,
      'Employees': d?.employees
    }));

    const csv = Papa?.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Custom_Compliance_Report_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
  };

  const exportToPDF = () => {
    alert('PDF export: Executive summary with PNG Kina currency formatting');
  };

  const exportToPowerPoint = () => {
    alert('PowerPoint export: Presentation template with charts and tables');
  };

  const handleApplyTemplate = (template) => {
    setSelectedMetrics({
      pngEmploymentAct: template?.includeViolationTrends,
      nasfundAdherence: template?.includeDepartmentMetrics,
      swtAccuracy: template?.includeCostAnalysis,
      leavePolicyViolations: template?.includeDlirReadiness,
      payrollCompliance: true
    });
    setShowTemplateLibrary(false);
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />

      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-[1600px] mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
              <Icon name="FileText" size={32} color="var(--color-primary)" />
              Custom Compliance Report Builder
            </h1>
            <Button
              variant="outline"
              iconName="BookTemplate"
              onClick={() => setShowTemplateLibrary(!showTemplateLibrary)}
            >
              Template Library
            </Button>
          </div>
          <p className="text-muted-foreground">
            Create tailored compliance reports with precise metric selection and flexible formatting options
          </p>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md flex items-start gap-2 mb-6">
            <Icon name="AlertCircle" size={18} className="mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {showTemplateLibrary && (
          <div className="mb-6">
            <TemplateLibrary onApplyTemplate={handleApplyTemplate} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-card rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Settings" size={20} />
                Report Configuration
              </h2>

              <div className="space-y-6">
                <MetricSelectionTree
                  selectedMetrics={selectedMetrics}
                  onChange={setSelectedMetrics}
                />

                <DateRangePicker
                  dateRange={dateRange}
                  onChange={setDateRange}
                />

                <DepartmentFilterMatrix
                  selectedDepartments={selectedDepartments}
                  onChange={setSelectedDepartments}
                />

                <ExportFormatSelector
                  selectedFormat={exportFormat}
                  onChange={setExportFormat}
                />

                <div className="pt-4 border-t border-border space-y-3">
                  <Button
                    variant="primary"
                    iconName="Download"
                    onClick={handleExportReport}
                    loading={loading}
                    disabled={!dateRange?.startDate || !dateRange?.endDate}
                    fullWidth
                  >
                    Export Report
                  </Button>
                  <Button
                    variant="outline"
                    iconName="Save"
                    fullWidth
                  >
                    Save as Template
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <ReportPreviewPanel
              reportData={reportData}
              loading={previewLoading}
              selectedMetrics={selectedMetrics}
              dateRange={dateRange}
            />
          </div>
        </div>
      </main>

      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </div>
  );
};

export default CustomComplianceReportBuilder;